import React, { useState, useEffect, useRef, useContext } from "react";
import { parseISO, format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import PersonIcon from "@material-ui/icons/Person";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";


import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
import useQueues from "../../hooks/useQueues";
import { AuthContext } from "../../context/Auth/AuthContext";
import { createFilterOptions } from '@mui/material/Autocomplete';
// import AsyncSelect from "../AsyncSelect";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	dialogPaper: {
		borderRadius: 12,
		overflow: "hidden",
		maxWidth: 760,
		width: "100%",
		maxHeight: "90vh",
		display: "flex",
		flexDirection: "column",
	},
	dialogTitle: {
		background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
		color: theme.palette.primary.contrastText,
		padding: theme.spacing(1.5, 2),
		display: "flex",
		alignItems: "center",
		gap: 10,
		fontSize: "1rem",
		fontWeight: 600,
	},
	dialogTitleIcon: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: 28,
		height: 28,
		borderRadius: "50%",
		backgroundColor: "rgba(255,255,255,0.2)",
	},
	dialogContent: {
		padding: theme.spacing(2, 2.5, 1.5),
		overflowY: "auto",
		flex: "1 1 auto",
		maxHeight: "calc(90vh - 120px)",
	},
	sectionTitle: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		fontSize: "0.85rem",
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(1),
		marginTop: theme.spacing(0.5),
	},
	sectionIcon: {
		color: theme.palette.primary.main,
		fontSize: 18,
	},
	sectionDivider: {
		border: 0,
		borderTop: `1px solid ${theme.palette.divider}`,
		margin: theme.spacing(1.5, 0, 1.25),
	},
	fieldDense: {
		"& .MuiOutlinedInput-root": {
			borderRadius: 8,
			fontSize: "0.875rem",
		},
		"& .MuiInputLabel-outlined": {
			fontSize: "0.875rem",
		},
		"& .MuiOutlinedInput-input": {
			paddingTop: 10.5,
			paddingBottom: 10.5,
		},
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},
	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		gap: 8,
		marginBottom: 6,
	},
	extraInfoBox: {
		backgroundColor: theme.palette.action.hover,
		borderRadius: 10,
		padding: theme.spacing(1.25),
		display: "flex",
		justifyContent: "center",
		marginTop: 4,
	},
	addExtraBtn: {
		textTransform: "none",
		fontWeight: 600,
		borderRadius: 8,
		padding: "6px 16px",
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
	},
	systemInfo: {
		backgroundColor: theme.palette.action.hover,
		borderRadius: 8,
		padding: theme.spacing(1, 1.25),
		fontSize: "0.8rem",
		color: theme.palette.text.secondary,
		marginBottom: 4,
	},
	switchRow: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(0.5),
		fontSize: "0.85rem",
	},
	dialogActions: {
		padding: theme.spacing(1.25, 2),
		backgroundColor: theme.palette.grey[50],
		borderTop: `1px solid ${theme.palette.divider}`,
		gap: 8,
		flexShrink: 0,
	},
	cancelBtn: {
		textTransform: "none",
		borderRadius: 8,
		fontWeight: 600,
	},
	saveBtn: {
		textTransform: "none",
		borderRadius: 8,
		fontWeight: 600,
		minWidth: 96,
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const formatDateForInput = (date) => {
	if (!date) return '';
	
	// Se já está no formato YYYY-MM-DD, retorna como está
	if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return date;
	}
	
	// Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
	const d = new Date(date);
	if (isNaN(d.getTime())) return '';
	
	// Usar métodos locais para evitar problemas de timezone
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	
	return `${year}-${month}-${day}`;
};
  
const parseDateFromInput = (dateString) => {
	if (!dateString) return null;
	
	// Se já está no formato YYYY-MM-DD, retorna como está
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return dateString;
	}
	
	// Se é uma data ISO, extrai apenas a parte da data
	if (dateString.includes('T')) {
		return dateString.split('T')[0];
	}
	
	return dateString;
};

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(250, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		disableBot: false,
		lgpdAcceptedAt: "",
		birthDate: ""
	};

	const [contact, setContact] = useState(initialState);
	const [disableBot, setDisableBot] = useState(false);
	const { user } = useContext(AuthContext);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [activeFloup, setActiveFloup] = useState(null);
	const [loadingFloup, setLoadingFloup] = useState(false);
	const [autocompleteOpen, setAutocompleteOpen] = useState(false);
	const [selectedTags, setSelectedTags] = useState([]);

	const { findAll: findAllQueues } = useQueues();

	// Configurar filterOptions para busca no servidor
	const filterOptions = createFilterOptions({
		trim: true,
	});

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Carregar todas as filas quando o modal abrir
	useEffect(() => {
		if (!open) return;
		
		const loadAllQueues = async () => {
			try {
				const list = await findAllQueues();
				if (isMounted.current) {
					setAllQueues(list);
					// Se não há usuário selecionado, mostrar todas as filas
					if (!selectedUser) {
						setQueues(list);
					}
				}
			} catch (err) {
				if (isMounted.current) {
					toastError(err);
				}
			}
		};

		loadAllQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Buscar usuários quando o searchParam mudar
	useEffect(() => {
		console.log("[ContactModal] useEffect searchParam:", { searchParam, open, length: searchParam?.length });
		
		if (!open) {
			setOptions([]);
			setLoading(false);
			setSearchParam("");
			return;
		}
		
		if (!searchParam || searchParam.length < 3) {
			setOptions([]);
			setLoading(false);
			return;
		}
		
		const delayDebounceFn = setTimeout(() => {
			console.log("[ContactModal] Buscando usuários com searchParam:", searchParam);
			setLoading(true);
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam, showAll: true },
					});
					if (isMounted.current) {
						console.log("[ContactModal] Usuários encontrados:", data.users?.length || 0, data.users);
						const users = data.users || [];
						setOptions(users);
						setLoading(false);
						console.log("[ContactModal] Options atualizadas - total:", users.length);
						// Abrir dropdown se houver opções e o campo ainda estiver focado
						if (users.length > 0 && searchParam.length >= 3) {
							setTimeout(() => {
								if (isMounted.current) {
									setAutocompleteOpen(true);
									console.log("[ContactModal] Forçando abertura do dropdown");
								}
							}, 100);
						}
					}
				} catch (err) {
					console.error("[ContactModal] Erro ao buscar usuários:", err);
					if (isMounted.current) {
						setLoading(false);
						toastError(err);
					}
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, open]);

	useEffect(() => {
		const fetchContact = async () => {
		  if (initialValues) {
			setContact(prevState => {
			  return { 
				...prevState, 
				...initialValues,
				// Formatar a data corretamente
				birthDate: formatDateForInput(initialValues.birthDate)
			  };
			});
		  }

			if (!contactId) {
				setActiveFloup(null);
				return;
			}

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({
						...data,
						birthDate: formatDateForInput(data.birthDate)
				});
					setDisableBot(data.disableBot)

					// Preenche automaticamente os campos de Wallet e Queue
					if (data.contactWallets && data.contactWallets.length > 0) {
						const wallet = data.contactWallets[0].wallet;
						const queue = data.contactWallets[0].queue;

						// Buscar as filas do usuário para permitir mudança
						const fetchUserQueues = async () => {
							try {
								// Buscar usuário específico para obter suas filas
								const { data: userData } = await api.get(`/users/${wallet.id}`);
								if (userData && Array.isArray(userData.queues) && userData.queues.length > 0) {
									if (isMounted.current) {
										setQueues(userData.queues);
										console.log("[ContactModal] Filas carregadas do usuário:", userData.queues.length);
									}
								} else {
									// Tentar buscar pela lista de usuários
									const { data: listData } = await api.get("/users/", {
										params: { searchParam: wallet.name, showAll: true },
									});
									const user = listData.users?.find(u => u.id === wallet.id);
									if (user && Array.isArray(user.queues) && user.queues.length > 0) {
										if (isMounted.current) {
											setQueues(user.queues);
											console.log("[ContactModal] Filas carregadas da lista:", user.queues.length);
										}
									} else {
										// Se não encontrar filas do usuário, usar apenas a fila atual
										if (isMounted.current) {
											setQueues([{ id: queue.id, name: queue.name }]);
										}
									}
								}
							} catch (err) {
								console.error("[ContactModal] Erro ao buscar filas do usuário:", err);
								// Em caso de erro, usar apenas a fila atual
								if (isMounted.current) {
									setQueues([{ id: queue.id, name: queue.name }]);
								}
							}
						};

						const userObj = {
							id: wallet.id,
							name: wallet.name,
						};
						
						setSelectedUser(userObj);
						setSelectedQueue(queue.id);
						// ✅ IMPORTANTE: Sincronizar searchParam com o nome do usuário selecionado
						setSearchParam(wallet.name || "");

						// Buscar filas do usuário
						fetchUserQueues();
					} else {
						// Limpa os valores quando não há contactWallets
						setSelectedUser(null);
						setSelectedQueue(null);
						// Mostrar todas as filas quando não há usuário selecionado
						if (allQueues.length > 0) {
							setQueues(allQueues);
						}
					}
				}

				// Buscar Floup ativo do contato
				setLoadingFloup(true);
				try {
					const floupResponse = await api.get(`/plugins/floup/contact/${contactId}/active`);
					if (isMounted.current) {
						setActiveFloup(floupResponse.data);
					}
				} catch (floupErr) {
					// Se não encontrar Floup ativo, não é erro, apenas não há Floup
					if (isMounted.current) {
						setActiveFloup(null);
					}
				} finally {
					if (isMounted.current) {
						setLoadingFloup(false);
					}
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setSelectedUser(null);
		setSelectedQueue(null);
		setQueues([]);
		setAllQueues([]);
		setSearchParam("");
		setOptions([]);
		setActiveFloup(null);
		setLoading(false);
		setAutocompleteOpen(false);
		setSelectedTags([]);
	};

	const handleSaveContact = async values => {
		try {
		  // Preparar os dados com a data corretamente formatada
		  const contactData = {
			...values,
			disableBot: disableBot,
			birthDate: parseDateFromInput(values.birthDate)
		  };
	  
		  if (contactId) {
			if (!selectedUser && !selectedQueue) {
			  delete contact.contactWallets;
			  await api.delete(`/contacts/wallet/${contactId}`);
			}
	  
			const { contactWallets, ...valuesWithoutWallets } = contactData;
			delete contact.contactWallets;
	  
			await api.put(`/contacts/${contactId}`, contactData);
	  
			if (selectedUser && selectedQueue && selectedUser !== null && selectedQueue !== null) {
			  await api.put(`/contacts/wallet/${contactId}`, {
				wallets: {
				  userId: selectedUser.id,
				  queueId: selectedQueue,
				},
			  });
			}
	  
			handleClose();
	  
		  } else {
			delete contactData.contactWallets;
	  
			const { data } = await api.post("/contacts", contactData);
	  
			// Sincronizar tags se houver tags selecionadas
			if (data.id && selectedTags && selectedTags.length > 0) {
				try {
					await api.post(`/tags/sync`, {
						contactId: data.id,
						tags: selectedTags
					});
				} catch (err) {
					console.error('[ContactModal] Erro ao sincronizar tags:', err);
					// Não bloquear o salvamento se houver erro nas tags
				}
			}
	  
			if (data.id && selectedUser && selectedQueue) {
			  await api.put(`/contacts/wallet/${data.id}`, {
				wallets: {
				  userId: selectedUser.id,
				  queueId: selectedQueue,
				},
			  });
			}
	  
			if (onSave) {
			  onSave(data);
			}
	  
			handleClose();
		  }
		  toast.success(i18n.t("contactModal.success"));
		} catch (err) {
		  toastError(err);
		}
	  };

	return (
		<div className={classes.root}>
			<Dialog 
				open={open} 
				onClose={handleClose} 
				maxWidth="md" 
				scroll="paper"
				fullWidth
				disableEnforceFocus={true}
				disableAutoFocus={true}
				disableRestoreFocus={true}
				PaperProps={{
					className: classes.dialogPaper,
					style: {
						zIndex: 10003,
						position: 'fixed'
					}
				}}
				BackdropProps={{
					style: {
						zIndex: 10002,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						position: 'fixed'
					}
				}}
				style={{
					zIndex: 10003,
					position: 'fixed'
				}}
			>
				<DialogTitle
					id="form-dialog-title"
					disableTypography
					className={classes.dialogTitle}
				>
					<span className={classes.dialogTitleIcon}>
						<PersonIcon style={{ fontSize: 18 }} />
					</span>
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent className={classes.dialogContent} dividers={false}>
								<Typography className={classes.sectionTitle}>
									<PersonIcon className={classes.sectionIcon} />
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>

								<Grid container spacing={1}>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.name")}
											name="name"
											autoFocus
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											fullWidth
											className={classes.fieldDense}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.number")}
											name="number"
											error={touched.number && Boolean(errors.number)}
											helperText={touched.number && errors.number}
											placeholder="5513912344321"
											variant="outlined"
											margin="dense"
											fullWidth
											className={classes.fieldDense}
										/>
									</Grid>
									<Grid item xs={12}>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.email")}
											name="email"
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											placeholder="Email address"
											fullWidth
											margin="dense"
											variant="outlined"
											className={classes.fieldDense}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											label="LID (Linked Device ID)"
											value={contact.lid || ""}
											disabled
											fullWidth
											margin="dense"
											variant="outlined"
											className={classes.fieldDense}
											helperText="Identificador do dispositivo vinculado"
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="Data de Nascimento"
											name="birthDate"
											type="date"
											InputLabelProps={{
												shrink: true,
											}}
											fullWidth
											margin="dense"
											variant="outlined"
											className={classes.fieldDense}
											helperText="Data de nascimento para mensagens automáticas de aniversário"
											onChange={(e) => {
												const formattedDate = parseDateFromInput(e.target.value);
												setFieldValue('birthDate', formattedDate);
											}}
										/>
									</Grid>
								</Grid>

								<hr className={classes.sectionDivider} />

								<Typography className={classes.sectionTitle}>
									Tags
								</Typography>
								<div>
									<TagsContainer 
										key={contactId || 'new-contact'} 
										contact={
											contactId 
												? contact 
												: { 
													...contact, 
													tags: selectedTags,
													onTagsChange: (tags) => setSelectedTags(tags)
												}
										} 
										className={classes.textField} 
									/>
								</div>

								{user?.allowDisableBot && (
									<div className={classes.switchRow}>
										<Switch
											size="small"
											checked={disableBot}
											onChange={() =>
												setDisableBot(!disableBot)
											}
											name="disableBot"
											color="primary"
										/>
										{i18n.t("contactModal.form.chatBotContact")}
									</div>
								)}

								<hr className={classes.sectionDivider} />

								<Typography className={classes.sectionTitle}>
									{i18n.t("contactModal.form.assignWallet")}
								</Typography>
								<Grid container spacing={1}>
									<Grid xs={12} sm={6} xl={6} item>
										<Autocomplete
											value={selectedUser}
											fullWidth
											disablePortal={false}
											getOptionLabel={(option) => option?.name || ""}
											isOptionEqualToValue={(option, value) => {
												if (!option || !value) return false;
												return option.id === value.id;
											}}
											onInputChange={(event, newInputValue, reason) => {
												console.log("[ContactModal] onInputChange:", { newInputValue, reason });
												setSearchParam(newInputValue || "");
												if (reason === 'clear' || !newInputValue || newInputValue.trim() === "") {
													if (reason === 'clear') {
														setSelectedUser(null);
														setQueues(allQueues);
														setSelectedQueue(null);
														setAutocompleteOpen(false);
													}
												} else if (reason === 'input' && newInputValue.length >= 3) {
													// Abrir dropdown quando o usuário está digitando (após buscar)
												}
											}}
											open={autocompleteOpen && options.length > 0 && !loading}
											onOpen={() => {
												console.log("[ContactModal] Autocomplete onOpen - options:", options.length);
												if (options.length > 0) {
													setAutocompleteOpen(true);
												}
											}}
											onClose={(event, reason) => {
												console.log("[ContactModal] Autocomplete onClose - reason:", reason);
												setAutocompleteOpen(false);
											}}
											onChange={async (e, newValue) => {
												console.log("[ContactModal] Usuário selecionado:", newValue);
												setSelectedUser(newValue);
												setAutocompleteOpen(false);
												if (newValue) {
													setSearchParam(newValue.name || "");
													console.log("[ContactModal] Filas do usuário no objeto:", newValue.queues);
													
													if (Array.isArray(newValue.queues) && newValue.queues.length > 0) {
														if (newValue.queues.length === 1) {
															setSelectedQueue(newValue.queues[0].id);
														} else {
															if (selectedQueue && !newValue.queues.some(q => q.id === selectedQueue)) {
																setSelectedQueue(null);
															}
														}
														setQueues(newValue.queues);
														console.log("[ContactModal] Filas atualizadas do objeto:", newValue.queues.length);
													} else {
														try {
															const { data: userData } = await api.get(`/users/${newValue.id}`);
															console.log("[ContactModal] Dados completos do usuário:", userData);
															if (userData && Array.isArray(userData.queues) && userData.queues.length > 0) {
																if (isMounted.current) {
																	setQueues(userData.queues);
																	if (userData.queues.length === 1) {
																		setSelectedQueue(userData.queues[0].id);
																	}
																	console.log("[ContactModal] Filas buscadas do servidor:", userData.queues.length);
																}
															} else {
																if (isMounted.current) {
																	setQueues(allQueues);
																}
															}
														} catch (err) {
															console.error("[ContactModal] Erro ao buscar filas:", err);
															if (isMounted.current) {
																setQueues(allQueues);
															}
														}
													}
												} else {
													setQueues(allQueues);
													setSelectedQueue(null);
													setSearchParam("");
												}
											}}
											options={options}
											filterOptions={(options, state) => {
												console.log("[ContactModal] filterOptions - options recebidas:", options.length, "inputValue:", state.inputValue);
												return options;
											}}
											freeSolo
											autoHighlight
											ListboxProps={{
												style: { maxHeight: '300px' },
												onMouseDown: (e) => {
													e.preventDefault();
												}
											}}
											componentsProps={{
												popper: {
													style: {
														zIndex: 13000
													},
													placement: 'bottom-start',
													disablePortal: false
												},
												paper: {
													style: {
														zIndex: 13000,
														textAlign: 'left',
														maxHeight: 300,
														overflow: 'auto'
													}
												}
											}}
											noOptionsText={
												searchParam.length < 3 
													? "Digite pelo menos 3 caracteres para buscar" 
													: loading 
														? "Buscando..." 
														: i18n.t("transferTicketModal.noOptions")
											}
											loading={loading}
											renderOption={(props, option) => {
												console.log("[ContactModal] renderOption chamado para:", option.name);
												return (
													<li {...props} key={option.id}>
														<span style={{ marginLeft: 8 }}>{option.name}</span>
													</li>
												);
											}}
											renderInput={(params) => {
												return (
													<TextField
														{...params}
														label={i18n.t("transferTicketModal.fieldLabel")}
														variant="outlined"
														margin="dense"
														className={classes.fieldDense}
														placeholder="Digite para buscar usuários"
														InputProps={{
															...params.InputProps,
															endAdornment: (
																<React.Fragment>
																	{loading ? (
																		<CircularProgress color="inherit" size={20} />
																	) : null}
																	{params.InputProps.endAdornment}
																</React.Fragment>
															),
														}}
													/>
												);
											}}
										/>
									</Grid>

									<Grid xs={12} sm={6} xl={6} item>
										<FormControl variant="outlined" fullWidth margin="dense" className={classes.fieldDense}>
											<InputLabel>
												{i18n.t("transferTicketModal.fieldQueueLabel")}
											</InputLabel>
											<Select
												value={selectedQueue || ""}
												onChange={(e) => setSelectedQueue(e.target.value)}
												label={i18n.t("transferTicketModal.fieldQueueLabel")}
												MenuProps={{
													style: {
														zIndex: 10004
													},
													PaperProps: {
														style: {
															zIndex: 10004
														}
													},
													disablePortal: false
												}}
											>
												{queues.length > 0 ? (
													queues.map((queue) => (
														<MenuItem key={queue.id} value={queue.id}>
															{queue.name}
														</MenuItem>
													))
												) : (
													<MenuItem disabled>
														{selectedUser 
															? "Usuário não possui filas associadas"
															: "Selecione um usuário primeiro"}
													</MenuItem>
												)}
											</Select>
										</FormControl>
									</Grid>
								</Grid>

								<hr className={classes.sectionDivider} />

								<Typography className={classes.sectionTitle}>
									<InfoOutlinedIcon className={classes.sectionIcon} />
									Informações do sistema
								</Typography>
								<div className={classes.systemInfo}>
									{i18n.t("contactModal.form.whatsapp")} {contact?.whatsapp ? contact?.whatsapp.name : "—"}
								</div>
								<div className={classes.systemInfo}>
									{i18n.t("contactModal.form.termsLGDP")} {contact?.lgpdAcceptedAt ? format(new Date(contact?.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm") : "—"}
								</div>

								{/* Campo Floup - Status e Etapa Atual */}
								<div style={{ marginTop: 8, marginBottom: 4 }}>
									<Typography className={classes.sectionTitle} style={{ marginBottom: 6 }}>
										Floup
									</Typography>
									{loadingFloup ? (
										<CircularProgress size={20} />
									) : activeFloup ? (
										<TextField
											fullWidth
											disabled
											variant="outlined"
											margin="dense"
											className={classes.fieldDense}
											value={`${activeFloup.name || 'Floup'} - Etapa ${(activeFloup.activeSchedule?.currentStepIndex || 0) + 1} de ${(activeFloup.steps || []).length}`}
											helperText={
												activeFloup.activeSchedule?.nextRunAt 
													? `Próxima execução: ${format(new Date(activeFloup.activeSchedule.nextRunAt), "dd/MM/yyyy 'às' HH:mm")}`
													: "Floup ativo"
											}
										/>
									) : (
										<TextField
											fullWidth
											disabled
											variant="outlined"
											margin="dense"
											className={classes.fieldDense}
											value="Nenhum Floup ativo"
											helperText="Este contato não está em nenhum Floup no momento"
										/>
									)}
								</div>

								<hr className={classes.sectionDivider} />

								<Typography className={classes.sectionTitle}>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={`${classes.textField} ${classes.fieldDense}`}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={`${classes.textField} ${classes.fieldDense}`}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraInfoBox}>
												<Button
													className={classes.addExtraBtn}
													variant="contained"
													disableElevation
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									onClick={handleClose}
									disabled={isSubmitting}
									variant="outlined"
									className={classes.cancelBtn}
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									disabled={isSubmitting}
									variant="contained"
									disableElevation
									className={`${classes.btnWrapper} ${classes.saveBtn}`}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;

